import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Edit2 } from "lucide-react";

/** Promoter — own sales list with edit/delete while pending_verification. */
export default function PromoterMySales() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/promoter/sales");
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this pending sale?")) return;
    try {
      await api.delete(`/promoter/sales/${id}`);
      toast.success("Sale deleted");
      load();
    } catch (e) {
      toast.error("Could not delete", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-3" data-testid="my-sales-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">My Sales</h1>
          <p className="text-xs text-slate-500 mt-0.5">Pending sales can still be edited.</p>
        </div>
        <Button onClick={() => navigate("/promoter/sales/new")}
                className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-sale-btn">
          <Plus size={14} className="mr-1" /> New
        </Button>
      </header>

      {loading && <div className="text-sm text-slate-500" data-testid="my-sales-loading">Loading…</div>}

      {!loading && rows.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-slate-500" data-testid="my-sales-empty">
            No sales submitted yet. Tap “New” to log your first sale.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((s) => (
          <Card key={s.id} data-testid={`my-sales-row-${s.id.slice(0, 8)}`}>
            <CardContent className="p-3 space-y-1">
              <div className="flex justify-between items-start">
                <div className="text-xs font-mono text-slate-500">{s.sale_date} · {s.invoice_number}</div>
                <StatusPill status={s.status} />
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {s.brand} {s.product_model} <span className="text-slate-500 font-normal">× {s.quantity}</span>
              </div>
              <div className="text-xs text-slate-600">{s.customer_name} · {s.customer_phone}</div>
              {s.status === "rejected" && s.rejection_reason && (
                <div className="mt-1 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800"
                     data-testid={`my-sales-rejection-${s.id.slice(0, 8)}`}>
                  <span className="font-semibold">Rejected:</span> {s.rejection_reason}
                </div>
              )}
              {s.status === "approved" && s.decided_by && (
                <div className="text-[10px] text-emerald-700 mt-0.5">
                  Approved by {s.decided_by}{s.decided_at ? ` · ${s.decided_at.slice(0, 10)}` : ""}
                </div>
              )}
              <div className="flex justify-between items-center pt-1">
                <div>
                  <div className="font-heading text-base font-bold text-slate-900" data-testid={`my-sales-net-${s.id.slice(0, 8)}`}>
                    Rs. {Number(s.net_sales_amount || 0).toLocaleString()}
                    <span className="text-[10px] text-slate-500 ml-1 font-normal">net</span>
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Gross Rs. {Number(s.invoice_amount || 0).toLocaleString()} · VAT Rs. {Number(s.vat_amount || 0).toLocaleString()}
                  </div>
                </div>
                {s.status === "pending_verification" && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline"
                            onClick={() => navigate(`/promoter/sales/${s.id}/edit`)}
                            data-testid={`my-sales-edit-${s.id.slice(0, 8)}`}>
                      <Edit2 size={12} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => remove(s.id)}
                            className="text-rose-700 hover:bg-rose-50"
                            data-testid={`my-sales-delete-${s.id.slice(0, 8)}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const c = {
    pending_verification: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    adjusted: "bg-indigo-100 text-indigo-800",
  }[status] || "bg-slate-100 text-slate-700";
  return <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${c}`}>{(status || "").replace("_", " ")}</span>;
}
